"use client";

import MagneticButton from "./MagneticButton";
import AmbientCanvas from "./AmbientCanvas";
import BottleVisual from "./BottleVisual";

interface Product {
  id: string;
  name: string;
  subtitle: string;
  tagline: string;
  image: string;
  accent: string;
  price: string;
  edition: string;
  origin: string;
  notes: { top: string[]; heart: string[]; base: string[] };
  specs: { label: string; value: string }[];
  description: string;
}

const products: Product[] = [
  {
    id: "belladonna",
    name: "Belladonna",
    subtitle: "Pour Femme · Eau de Parfum",
    tagline: "Grace in every drop. Allure that lingers.",
    image: "/images/pink_bottle.png",
    accent: "#c47a8b",
    price: "$310",
    edition: "Batch Nº 001",
    origin: "Carpathian Foothills — Moon Harvest",
    notes: {
      top: ["Nightshade", "Black Currant", "Pink Pepper"],
      heart: ["Tuberose", "Jasmine Sambac", "Incense"],
      base: ["Dark Amber", "Patchouli", "Sandalwood"],
    },
    specs: [
      { label: "Sillage", value: "Veiled" },
      { label: "Longevity", value: "12h+" },
      { label: "Concentration", value: "28%" },
      { label: "Volume", value: "50ml" },
    ],
    description:
      "A nocturne in crystal. Night-blooming florals wrapped in incense and dark amber — worn in candlelight, remembered at dawn.",
  },
  {
    id: "wolfsbane",
    name: "Wolfsbane",
    subtitle: "Pour Homme · Eau de Parfum",
    tagline: "Untamed by nature. Unforgettable by choice.",
    image: "/images/blue_bottle.png",
    accent: "#5b7d9b",
    price: "$285",
    edition: "Batch Nº 002",
    origin: "Volcanic Highlands — Fire-cured",
    notes: {
      top: ["Black Pepper", "Bergamot", "Cardamom"],
      heart: ["Smoked Vetiver", "Dark Iris", "Leather"],
      base: ["Oud", "Amber", "Musk"],
    },
    specs: [
      { label: "Sillage", value: "Distinct" },
      { label: "Longevity", value: "14h+" },
      { label: "Concentration", value: "30%" },
      { label: "Volume", value: "50ml" },
    ],
    description:
      "Smoke, earth, and sinew. A leather-and-oud noir that never announces itself — it simply never leaves.",
  },
];

function NotesPyramid({ notes, accent }: { notes: Product["notes"]; accent: string }) {
  const groups = [
    { label: "Top", notes: notes.top },
    { label: "Heart", notes: notes.heart },
    { label: "Base", notes: notes.base },
  ];
  return (
    <div className="border-t border-b border-[#221d17]/8">
      {groups.map((group, i) => (
        <div
          key={group.label}
          className={`flex items-center justify-between py-3 ${i < 2 ? "border-b border-[#221d17]/8" : ""}`}
        >
          <span className="text-[8px] tracking-[0.3em] uppercase w-14" style={{ color: `${accent}` }}>
            {group.label}
          </span>
          <span className="text-[10px] text-[#6f6759]/75 text-right leading-relaxed">
            {group.notes.join(" · ")}
          </span>
        </div>
      ))}
    </div>
  );
}

function ProductCard({
  product,
  onOrder,
}: {
  product: Product;
  onOrder: (name: string) => void;
}) {
  const flip = product.id === "wolfsbane";
  return (
    <article className="collection-card relative">
      {/* Scent-specific ambient — minimal, bright */}
      <AmbientCanvas
        variant="petals"
        accent={product.accent}
        density={0.4}
        className="opacity-40 mix-blend-multiply"
      />
      <div
        className="orb-glow -bottom-20 -right-20 w-[26rem] h-[26rem] opacity-70"
        style={{ background: `radial-gradient(circle, ${product.accent}1a 0%, transparent 65%)` }}
      />

      {/* Light showcase frame */}
      <div className="relative gold-frame gold-glow-hover reveal-blur p-6 md:p-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          {/* Bottle on marble podium with silk behind */}
          <div className={flip ? "relative lg:order-2" : "relative"}>
            <div
              className="silk left-[-8%] top-[6%] w-[24rem] h-[20rem]"
              style={{
                background: `radial-gradient(ellipse at center, ${product.accent}2e, ${product.accent}12 55%, transparent 75%)`,
              }}
              aria-hidden="true"
            />
            <BottleVisual
              src={product.image}
              alt={`${product.name} bottle`}
              name={product.name}
              glow={product.accent}
              showNameplate={false}
              sizes="(max-width: 1024px) 90vw, 40vw"
            />
          </div>

          {/* Details */}
          <div className={flip ? "relative lg:order-1" : "relative"}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-px" style={{ backgroundColor: `${product.accent}` }} />
              <p className="text-[9px] tracking-[0.3em] uppercase" style={{ color: `${product.accent}` }}>
                {product.subtitle}
              </p>
            </div>

            <h3
              className="font-serif text-4xl md:text-5xl text-[#221d17] mb-2"
              style={{ fontWeight: 300 }}
            >
              {product.name}
            </h3>

            <p className="text-sm text-[#6f6759]/70 italic mb-5">{product.tagline}</p>
            <p className="text-sm text-[#6f6759]/80 font-light leading-relaxed max-w-md mb-6">
              {product.description}
            </p>

            {/* Origin + edition */}
            <div className="flex items-center justify-between mb-6">
              <span className="font-mono text-[9px] tracking-[0.2em] uppercase text-[#6f6759]/60">
                {product.origin}
              </span>
              <span className="font-mono text-[9px] tracking-[0.2em] uppercase" style={{ color: `${product.accent}` }}>
                {product.edition}
              </span>
            </div>

            {/* Notes pyramid */}
            <div className="mb-6">
              <NotesPyramid notes={product.notes} accent={product.accent} />
            </div>

            {/* Specs grid */}
            <div className="grid grid-cols-4 gap-3 mb-8">
              {product.specs.map((spec) => (
                <div key={spec.label} className="flex flex-col gap-1">
                  <span className="text-[7px] tracking-[0.25em] uppercase text-[#6f6759]/55">
                    {spec.label}
                  </span>
                  <span className="text-[11px] text-[#221d17]/80">{spec.value}</span>
                </div>
              ))}
            </div>

            <MagneticButton strength={0.2}>
              <button onClick={() => onOrder(product.name)} className="btn-elegant" data-cursor="pointer">
                <span>Order {product.name}</span>
              </button>
            </MagneticButton>
          </div>
        </div>
      </div>
    </article>
  );
}

export default function Collection({
  onOrderClick,
}: {
  onOrderClick?: (product: string) => void;
}) {
  return (
    <section
      id="collection"
      aria-label="The Collection"
      className="relative py-24 md:py-36 overflow-hidden"
      style={{ background: "linear-gradient(180deg, #f9f6f0 0%, #f2ecdf 100%)" }}
    >
      <div className="absolute inset-0 marble opacity-60" aria-hidden="true" />
      <div className="hairline-grid absolute inset-0 opacity-25" aria-hidden="true" />

      {/* Silk ambience */}
      <div
        className="silk left-[-6%] top-[8%] w-[38rem] h-[26rem]"
        style={{ background: "radial-gradient(ellipse at center, rgba(255,252,245,0.9), rgba(248,240,226,0.5) 60%, transparent 75%)" }}
        aria-hidden="true"
      />
      <div
        className="silk right-[-8%] bottom-[6%] w-[36rem] h-[26rem]"
        style={{ background: "radial-gradient(ellipse at center, rgba(216,186,128,0.25), rgba(216,186,128,0.08) 60%, transparent 78%)", animationDelay: "-11s" }}
        aria-hidden="true"
      />

      {/* Header */}
      <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 mb-16 md:mb-24">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <p className="reveal-blur text-[10px] tracking-[0.4em] uppercase text-[#8a6a2c]/70 mb-4">
              The Collection
            </p>
            <h2
              className="reveal-blur font-serif text-4xl md:text-6xl lg:text-7xl tracking-tight text-[#221d17]"
              style={{ fontWeight: 300, transitionDelay: "0.1s" }}
            >
              Two Scents.
              <br />
              <span className="text-shimmer-gold">One Shadow.</span>
            </h2>
          </div>
          <p className="reveal-blur text-sm text-[#6f6759]/70 max-w-sm font-light" style={{ transitionDelay: "0.2s" }}>
            Two signatures, one house. Each blend aged in darkness, sealed in
            hand-finished crystal, and released in numbered batches.
          </p>
        </div>
        <div className="section-divider w-16 h-px mt-8 bg-gradient-to-r from-[#c9a84c]/60 to-transparent" />
      </div>

      {/* Products */}
      <div className="relative max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="flex flex-col gap-16 md:gap-24">
          {products.map((product) => (
            <ProductCard
              key={product.id}
              product={product}
              onOrder={onOrderClick ?? (() => {})}
            />
          ))}
        </div>
      </div>
    </section>
  );
}